/**
 * GameState — the entire universe in one serializable object.
 *
 * Rules:
 *   1. Every field MUST be JSON-serializable (no functions, no class instances
 *      except via explicit serialize/deserialize hooks).
 *   2. Every change goes through `reduce(state, action)`. Never mutate.
 *   3. Adding a field = bump SAVE_VERSION + author migration in saves.ts.
 *
 * # V2 (current)
 *
 * GameState v2 introduces the real-time isometric action-roguelite combat
 * shape. Replaces v1's turn-based card combat. Major changes from v1:
 *
 *   - `combat: CombatState | null` — null when not in a run, populated
 *     during an active run. V1's `combat: CombatStateV1` (always present,
 *     turn-based shape) is gone.
 *
 *   - `loadout: Loadout` — new field. Player's chosen Architecture, ability
 *     hotbar, Subroutine slots, and cosmetic palette. Replaces v1's `deck`
 *     concept (card combat).
 *
 *   - V1's `Combatant` and `CombatStateV1` types remain as `LegacyCombatant`
 *     and `LegacyCombatStateV1` in this file, used only by the v1→v2
 *     migration. Production code does not import these legacy types.
 *
 * @see {@link ./state/types.ts} — v2 type definitions (Loadout, CombatEntity, etc.)
 * @see {@link ./save/migrations/v1_to_v2.ts} — migration that produces v2 shapes
 */

import type { RngSnapshot } from './types';
import type { CombatState, Loadout } from './state/types';
import { DEFAULT_LOADOUT } from './state/types';

// ─────────────────────────────────────────────────────────────────────────────
// SAVE_VERSION
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Current save schema version. Bumped from 1 to 2 with the combat genre
 * pivot (May 2026).
 *
 * When this number changes, a single-step migration v_(n-1) → v_n MUST be
 * registered in `save/migrations/registerAll.ts`. Tests verify that
 * `registerAllMigrations()` produces a complete chain from v1 to
 * SAVE_VERSION.
 */
export const SAVE_VERSION = 2;

// ─────────────────────────────────────────────────────────────────────────────
// V2 GameState
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The top-level game state. Everything that defines "the player's universe"
 * at any moment lives here.
 *
 * # Fields
 *
 *   - `version` — schema version. Equals SAVE_VERSION at write time.
 *
 *   - `runSeed` — the seed used to start the most recent run. Combat/loot/
 *     map RNG forks deterministically from this. Number, not bigint, because
 *     53-bit seed entropy is plenty for replay-anchor purposes.
 *
 *   - `rng` — meta-progression RNG state, snapshotted for save/replay.
 *     Distinct from `combat.combatRngState` which is a per-run fork.
 *
 *   - `combat` — active run state, or `null` if no run is active. The new
 *     v2 shape (real-time, tick-based, fixed-point integer math).
 *
 *   - `loadout` — player's combat configuration: chosen Architecture,
 *     Q/E/R/F ability hotbar, up to 3 Subroutine slots, cosmetic palette.
 *     Persists across runs (player can change loadout between runs but
 *     not mid-run).
 *
 * # Future fields
 *
 * Currencies, unlocks, achievements, federation membership, prestige
 * counters all land in their own SAVE_VERSION bumps. Each gets a migration
 * that initializes it from sensible defaults; no in-progress run is lost.
 */
export interface GameState {
  readonly version: number;
  readonly runSeed: number;
  readonly rng: RngSnapshot;
  readonly combat: CombatState | null;
  readonly loadout: Loadout;
}

/**
 * Construct an initial GameState for a brand-new player. Used when no save
 * exists in IndexedDB.
 *
 * `combat` is null (no active run). `loadout` is the default (Vector
 * architecture, no Subroutines, default palette). `rng` snapshot is
 * placeholder (`s0: '0', s1: '0'`); the reducer populates real RNG state
 * on first action that consumes RNG.
 */
export function createInitialState(seed: number): GameState {
  return {
    version: SAVE_VERSION,
    runSeed: seed,
    rng: { s0: '0', s1: '0' },
    combat: null,
    loadout: DEFAULT_LOADOUT,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Re-exports for ergonomics
//
// V2 types from state/types.ts are re-exported here so consumers can keep
// importing `{ GameState, CombatState, Loadout, ... }` from a single place.
// ─────────────────────────────────────────────────────────────────────────────

export type {
  // Combat shape
  CombatState,
  CombatEntity,
  EntityKind,
  Projectile,
  ProjectileVisualKind,
  ActiveEffect,
  EffectKind,
  EffectPayload,
  AIState,
  TileMap,
  SpawnPoint,
  ExitZone,
  HazardZone,
  CombatInputEntry,
  // Loadout shape
  Loadout,
  AbilitySlot,
  AbilityId,
  SubroutineId,
  // Identifier types
  ArchitectureId,
  FactionId,
  TeamId,
  BiomeId,
  PaletteId,
  EntityId,
  ProjectileId,
  EffectId,
} from './state/types';

export { DEFAULT_LOADOUT } from './state/types';

// ─────────────────────────────────────────────────────────────────────────────
// Legacy V1 types — used ONLY by the v1→v2 migration
//
// These are NOT exported as part of the public API. The migration module
// imports them directly. Production code MUST NOT use these types — they
// represent an obsolete combat shape that exists in the codebase only to
// type the migration's input.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * V1 combatant — the turn-based card combat shape.
 *
 * Used only by `migrations/v1_to_v2.ts` to type the legacy save's
 * `combat.allies[]` and `combat.enemies[]` fields during migration parsing.
 *
 * @internal
 * @deprecated V1 only. Use `CombatEntity` for v2+.
 */
export interface LegacyCombatant {
  readonly id: string;
  readonly name: string;
  readonly hp: number;
  readonly hpMax: number;
  readonly attack: number;
  readonly defense: number;
  readonly initiative: number;
}

/**
 * V1 combat phase — turn-based state machine.
 *
 * @internal
 * @deprecated V1 only.
 */
export type LegacyCombatPhase = 'idle' | 'in_combat' | 'victory' | 'defeat';

/**
 * V1 combat state — turn-based card combat shape.
 *
 * Used only by `migrations/v1_to_v2.ts` to type the legacy save's
 * `gameState.combat` field during migration parsing.
 *
 * @internal
 * @deprecated V1 only. Use `CombatState` for v2+.
 */
export interface LegacyCombatStateV1 {
  readonly phase: LegacyCombatPhase;
  readonly allies: readonly LegacyCombatant[];
  readonly enemies: readonly LegacyCombatant[];
  readonly turn: number;
  readonly activeCombatantId: string | null;
  readonly log: readonly string[];
}

/**
 * V1 GameState — the shape of saves at version 1.
 *
 * Used only by `migrations/v1_to_v2.ts` to type the migration's input.
 *
 * @internal
 * @deprecated V1 only. Use `GameState` for v2+.
 */
export interface LegacyGameStateV1 {
  readonly version: 1;
  readonly runSeed: number;
  readonly rng: RngSnapshot;
  readonly combat: LegacyCombatStateV1;
}
